import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { SubPayoutDAO } from '../../../../../../core/services/dao/subPayoutDAO';
import { Payout } from 'anchor-shared/lib/model/entity/payout';
import { UserDAO } from '../../../../../../core/services/dao/userDAO';
import { DialogsService } from '../../../../../../core/services/dialog.service';
import { CharterDAO } from '../../../../../../core/services/dao/charterDAO';
import { PayoutPayeeSelection } from '../../../../../public/change-payout-payee-dialog/change-payout-payee-dialog.component';
import { PayoutDAO } from '../../../../../../core/services/dao/payoutDAO';
import { AppMessagingService } from '../../../../../../core/services/app-messaging.service';
import { NumberFormatterService } from '../../../../../../core/services/number.formatter.service';
import { ContainsSubscriptions } from '../../../../../../core/services/interfaces/contains-subscriptions';
import { Subscription } from 'rxjs/Rx';
import { unsubscribeFromAll } from '../../../../../../core/pure-functions/rx-helper';
import { SubPayoutType } from 'anchor-shared/lib/model/enum/sub-payout-type';
import { SubPayout } from 'anchor-shared/lib/model/entity/subPayout';
import { Charter } from 'anchor-shared/lib/model/entity/charter';

@Component({
  selector: 'app-payout-display',
  templateUrl: './payout-display.component.html',
  styleUrls: ['./payout-display.component.scss'],
})
export class PayoutDisplayComponent
  implements OnInit, ContainsSubscriptions, OnDestroy
{
  @Input() charter: Charter;
  @Input() fuelIncludedInCharter: Boolean;
  subs: Subscription[] = [];

  constructor(
    private userDAO: UserDAO,
    private subPayoutDAO: SubPayoutDAO,
    private dialogService: DialogsService,
    private charterDao: CharterDAO,
    private payoutDao: PayoutDAO,
    private appMessagingService: AppMessagingService,
    private formatter: NumberFormatterService,
  ) {}

  ngOnInit() {
    this.initSubPayouts();
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  changePayee(payout: Payout): void {
    const crewJSON = this.charter.crew;
    const payeeChangeSub = this.dialogService
      .SelectPayoutPayeeDialog(payout, crewJSON, this.charter)
      .subscribe((payeeSelection: PayoutPayeeSelection) => {
        if (payeeSelection) {
          if (payeeSelection.payeeType === 'Boat Owner') {
            payout.payee =
              this.charter.boat.owner.user.firstName +
              ' ' +
              this.charter.boat.owner.user.lastName;
            payout.payeePaypalHandle = this.charter.boat.owner.user.paypalEmail;
            payout.ownerId = this.charter.boat.ownerId;
            payout.userId = this.charter.boat.owner.user.id;
          } else if (payeeSelection.payeeType === 'Captain') {
            payout.payee =
              this.charter.captain.user.firstName +
              ' ' +
              this.charter.captain.user.lastName;
            payout.payeePaypalHandle = this.charter.captain.user.paypalEmail;
            payout.userId = this.charter.captain.user.id;
          } else if (payeeSelection.payeeType === 'Anchor') {
            payout.payee = 'Anchor';
            payout.payeePaypalHandle = '';
            payout.userId = null;
          } else if (payeeSelection.payeeType === 'Crew') {
            payout.payee =
              payeeSelection.user.firstName +
              ' ' +
              payeeSelection.user.lastName;
            payout.payeePaypalHandle = payeeSelection.user.paypalEmail;
            payout.userId = payeeSelection.user.id;
          } else if (payeeSelection.user) {
            payout.payee =
              payeeSelection.user.firstName +
              ' ' +
              payeeSelection.user.lastName;
            payout.payeePaypalHandle = payeeSelection.user.paypalEmail;
            payout.userId = payeeSelection.user.id;
          }

          const payoutUpdateSub = this.payoutDao
            .updateMultiplePayoutsByCharter([payout])
            .subscribe((updatedPayouts) => {
              console.log(updatedPayouts);
            });
          this.subs.push(payoutUpdateSub);
        }
      });
    this.subs.push(payeeChangeSub);
  }

  getPayoutDisplayName(payout: Payout): string {
    if (payout.name) {
      if (payout.name === 'Fuel Included') {
        if (this.fuelIncludedInCharter) {
          return 'Fuel Reimbursement';
        } else {
          return 'Fuel Reimbursement (Fuel Not Included)';
        }
      } else {
        return payout.name;
      }
    } else {
      return payout.type;
    }
  }

  updateSubPayoutNotes(subPayout: SubPayout): void {
    const payoutUpdateSub = this.subPayoutDAO
      .updateFromPartial(subPayout.id, {
        notes: subPayout.notes,
      })
      .subscribe((updatedPayouts) => {
        this.appMessagingService.openSnack(
          'Split Payout notes successfully updated',
        );
      });
    this.subs.push(payoutUpdateSub);
  }

  async splitPayoutIntoSubPayouts(parentPayout: any) {
    parentPayout.newSubPayouts = [];

    let evenSplitAmount = (
      parentPayout.actualPay / parentPayout.splitCount
    ).toFixed(2);

    for (let i = 0; i < parentPayout.splitCount; i++) {
      if (parentPayout.userId) {
        let userSub = this.userDAO
          .findUserById(parentPayout.userId)
          .subscribe((foundUser) => {
            parentPayout.newSubPayouts.push({
              basePay: evenSplitAmount,
              actualPay: evenSplitAmount,
              published: false,
              paid: false,
              notes: parentPayout.notes,
              userId:
                parentPayout.userId && i === 0 ? parentPayout.userId : null,
              payStubId: parentPayout.payStubId,
              parentId: parentPayout.id,
              user: foundUser,
            });
          });
        this.subs.push(userSub);
      } else {
        parentPayout.newSubPayouts.push({
          basePay: evenSplitAmount,
          actualPay: evenSplitAmount,
          published: false,
          paid: false,
          notes: parentPayout.notes,
          userId: parentPayout.userId && i === 0 ? parentPayout.userId : null,
          payStubId: parentPayout.payStubId,
          parentId: parentPayout.id,
          type:
            parentPayout.payee === 'Anchor' && i > 0
              ? parentPayout.type
              : 'Anchor',
        });
      }
    }
  }

  openChangePayeeDialog(subPayout: any) {
    const parsedCrew = this.formatter.parseJsonIfString(this.charter.crew);
    const subPayoutPayeeSub = this.dialogService
      .SelectPayoutPayeeDialog(subPayout, parsedCrew, this.charter)
      .subscribe((data) => {
        if (data) {
          subPayout.payeeType = data.payeeType;
          if (data.user) {
            subPayout.userId = data.user.id;
            subPayout.user = data.user;
            subPayout.type = SubPayoutType.USER;
          } else {
            subPayout.user = null;
            subPayout.userId = null;
            subPayout.type = SubPayoutType.ANCHOR;
          }
          const subUpdateSub = this.subPayoutDAO
            .updateMultiple([subPayout])
            .subscribe();
          this.subs.push(subUpdateSub);
        }
      });
    this.subs.push(subPayoutPayeeSub);
  }

  disableSubPayoutSave(parentPayout: any) {
    if (this.getRemainingSplitAllotment(parentPayout) < 0) {
      return true;
    }
    let allSplitsAssignedPay;
    let allSplitsHavePayees;
    if (parentPayout.newSubPayouts && parentPayout.newSubPayouts.length > 0) {
      allSplitsAssignedPay = parentPayout.newSubPayouts.every((subPayout) => {
        return subPayout.actualPay > 0;
      });
      allSplitsHavePayees = parentPayout.newSubPayouts.every((subPayout) => {
        return subPayout.userId || subPayout.type === SubPayoutType.ANCHOR;
      });
    } else {
      allSplitsHavePayees = false;
      allSplitsAssignedPay = false;
    }

    return !(allSplitsAssignedPay && allSplitsHavePayees);
  }

  savePayoutSplit(parentPayout: any) {
    if (parentPayout.editMode) {
      this.removeNetPayFromSubPayouts(parentPayout);
      const deletePayoutSub = this.subPayoutDAO
        .deleteMultiple(parentPayout.subPayouts)
        .subscribe(() => {
          const createSubPayoutSub = this.subPayoutDAO
            .createMultiple(parentPayout.newSubPayouts)
            .subscribe((newSubPayouts) => {
              if (newSubPayouts) {
                parentPayout.splitInProgress = false;
                parentPayout.subPayouts = newSubPayouts;
                this.initSubPayouts();
              }
            });
          this.subs.push(createSubPayoutSub);
        });
      this.subs.push(deletePayoutSub);
    } else {
      const createSubPayoutSub = this.subPayoutDAO
        .createMultiple(parentPayout.newSubPayouts)
        .subscribe((newSubPayouts) => {
          if (newSubPayouts) {
            parentPayout.splitInProgress = false;
            parentPayout.subPayouts = newSubPayouts;
            this.initSubPayouts();
          }
        });
      this.subs.push(createSubPayoutSub);
    }
  }

  private initSubPayouts() {
    this.charter.payouts.forEach((payout) => {
      // @ts-ignore
      payout.splitInProgress = false;
      // @ts-ignore
      payout.editMode = false;
      // @ts-ignore
      payout.splitCount = 2;
      // @ts-ignore
      payout.newSubPayouts = [];

      if (payout.subPayouts && payout.subPayouts.length > 0) {
        payout.subPayouts.forEach((subPayout) => {
          if (subPayout && subPayout.userId) {
            const userSub = this.userDAO
              .findUserById(subPayout.userId)
              .subscribe((foundUser) => {
                if (foundUser) {
                  subPayout.user = foundUser;
                }
                // @ts-ignore
                payout.newSubPayouts.push(Object.assign({}, subPayout));
              });
            this.subs.push(userSub);
          } else {
            // @ts-ignore
            payout.newSubPayouts.push(Object.assign({}, subPayout));
          }
        });
        // @ts-ignore
        payout.splitCount = payout.subPayouts.length;
      }
    });
  }

  cancelPayoutSplit(parentPayout: any) {
    parentPayout.newSubPayouts = [];
    if (parentPayout.subPayouts && parentPayout.subPayouts.length > 0) {
      parentPayout.subPayouts.forEach((subPayout) => {
        parentPayout.newSubPayouts.push(subPayout);
      });
    }
    parentPayout.splitInProgress = false;
  }

  saveProcessingFeePayout(payout: Payout) {
    const payoutUpdateSub = this.payoutDao
      .updateFromPartial(payout.id, {
        processingFee: payout.processingFee,
        actualPay: payout.actualPay,
        netPay: null,
      })
      .subscribe((updatedPayouts) => {
        this.appMessagingService.openSnack(
          'Payout Processing fee successfully updated',
        );
      });
    this.subs.push(payoutUpdateSub);
  }

  saveProcessingFeeSubPayout(subPayout: SubPayout) {
    const payoutUpdateSub = this.subPayoutDAO
      .updateFromPartial(subPayout.id, {
        processingFee: subPayout.processingFee,
        actualPay: subPayout.actualPay,
        netPay: null,
      })
      .subscribe((updatedPayouts) => {
        this.appMessagingService.openSnack(
          'Split Payout Processing fee successfully updated',
        );
      });
    this.subs.push(payoutUpdateSub);
  }

  getRemainingSplitAllotment(parentPayout: any) {
    let subPayoutsTotal = 0;
    if (!parentPayout.newSubPayouts) {
      parentPayout.newSubPayouts = [];
    }
    parentPayout.newSubPayouts.forEach((subPayout) => {
      if (subPayout.actualPay) {
        subPayoutsTotal += parseFloat(subPayout.actualPay);
      }
    });
    return parentPayout.actualPay - subPayoutsTotal;
  }

  private removeNetPayFromSubPayouts(parentPayout): void {
    if (parentPayout.newSubPayouts) {
      parentPayout.newSubPayouts.forEach((subPayout) => {
        subPayout.processingFee = true;
        subPayout.netPay = null;
      });
    }
  }
}
